import React, { useState, useEffect } from "react";
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { Checkbox, Stack, TableCell, TextField, Typography } from '@mui/material';
import { PausedIndicator } from '../../../Components/PausedIndicator';
import { SkuFulfillmentTypeSelect } from "../components/SkuFulfillmentTypeSelect";
import { SkuOfferingStatusSelect } from '../components/SkuOfferingStatusSelect';
import { DistributorInMarketDatePicker } from "../components/DistributorInMarketDatePicker";
import { DistributorEndInMarketDatePicker } from "../components/DistributorEndInMarketDatePicker";
import { useProductSettingsContext } from "../context/ProductSettingsContext";
import PropTypes from 'prop-types';

const enableInMarketDate = process.env.REACT_APP_ENABLE_IN_MARKET_DATE === 'true';
const enableSeasonalForecast = process.env.REACT_APP_ENABLE_SEASONAL_FORECAST === 'true';
const enablePauseIndicator = process.env.REACT_APP_ENABLE_PAUSE_INDICATOR === 'true';

export const DistributorTableRow = ({
    row,
    handleUpdateRow,
    onSaveProductSettingsDistributorOverride,
    isChanged,
}) => {
    const { onRowSelectClick, isRowSelected, productDefaults } = useProductSettingsContext();
    const [originalRow, setOriginalRow] = useState(row);

    // Baseline for onBlur/onKeyPress diffing. Resync only on two triggers:
    // row.id change (TableVirtuoso recycles this component instance across distributors), or
    // skuOfferingStatus/skuFulfillmentType arriving late from an async load.
    // Never resync on other row changes - row mutates on every keystroke, and resyncing
    // there would erase the baseline before onBlur can diff against it.
    useEffect(() => {
        if (!row) return;

        if (row.id !== originalRow?.id) {
            setOriginalRow({ ...row });
            return;
        }

        const statusJustArrived = !originalRow?.skuOfferingStatus && row.skuOfferingStatus;
        const fulfillmentJustArrived = !originalRow?.skuFulfillmentType && row.skuFulfillmentType;

        if (statusJustArrived || fulfillmentJustArrived) {
            setOriginalRow(previous => ({
                ...previous,
                skuOfferingStatus: row.skuOfferingStatus,
                skuFulfillmentType: row.skuFulfillmentType
            }));
        }
    }, [row, originalRow]);

    const onBlurHandler = async () => {
        await onSaveProductSettingsDistributorOverride(row, originalRow);
        await handleUpdateRow(row);
        setOriginalRow(row);
    };

    const onKeyPressHandler = async (event) => {
        if (event.key === "Enter") {
            await onSaveProductSettingsDistributorOverride(row, originalRow);
            setOriginalRow(row);
        }
    };

    return (<React.Fragment>
        <TableCell padding="checkbox">
            <Checkbox
                id={row?.id}
                color="primary"
                disabled={row?.legal === false}
                checked={isRowSelected(row)}
                onChange={event => onRowSelectClick(row, event.target.checked)} />
        </TableCell>
        <TableCell>
            <Stack direction='row' alignItems='center' spacing={1}>
                {enablePauseIndicator ? (row?.paused ? <PausedIndicator size={20} /> : <span style={{ width: 20, display: 'inline-block' }} />) : null}
                <Typography>{`${row?.sapId} - ${row?.name}`}</Typography>
            </Stack>
        </TableCell>
        <TableCell align="center" sx={{ padding: '0px' }}>
            {row?.legal === false ? (<CloseIcon />) : <></>}
        </TableCell>
        <TableCell align="center" sx={{ padding: '0px' }}>
            <TextField
                placeholder="Default"
                value={row.palletQuantity}
                size="small"
                type="number"
                disabled={row?.legal === false}
                onKeyPress={onKeyPressHandler}
                onChange={async (event) => {
                    if (!Number.isInteger(Number.parseFloat(event.target.value)) && event.target.value !== "") return;
                    await handleUpdateRow({ ...row, palletQuantity: event.target.value })
                }}
                onBlur={onBlurHandler}
                sx={{ width: 110 }}
            />
        </TableCell>
        <TableCell align="center" sx={{ padding: '0px' }}>
            <TextField
                placeholder="Default"
                value={row.palletDivisor}
                size="small"
                type="number"
                disabled={row?.legal === false}
                onKeyPress={onKeyPressHandler}
                onChange={async (event) => {
                    if (!Number.isInteger(Number.parseFloat(event.target.value)) && event.target.value !== "") return;
                    await handleUpdateRow({ ...row, palletDivisor: event.target.value })
                }}
                onBlur={onBlurHandler}
                sx={{ width: 110 }}
            />
        </TableCell>
        <TableCell align="center" sx={{ padding: '0px' }}>
            <TextField
                placeholder="Default"
                value={row.unitWeight}
                size="small"
                type="number"
                disabled={row?.legal === false}
                onKeyPress={onKeyPressHandler}
                onChange={async (event) => {
                    if (Number.isNaN(event.target.value) && event.target.value !== "") return;
                    await handleUpdateRow({ ...row, unitWeight: event.target.value })
                }}
                onBlur={onBlurHandler}
                sx={{ width: 110 }}
            />
        </TableCell>
        <TableCell align="center">
            <SkuOfferingStatusSelect
                row={row}
                originalRow={originalRow}
                handleUpdateRow={handleUpdateRow}
            />
        </TableCell>
        <TableCell align="center">
            <SkuFulfillmentTypeSelect
                handleUpdateRow={handleUpdateRow}
                isChanged={isChanged}
                row={row}
                originalRow={originalRow} />
        </TableCell>
        {enableSeasonalForecast && (
            <TableCell align="center" sx={{ padding: '0px' }}>
                {productDefaults?.seasonal ? <CheckIcon fontSize="small" color="success" titleAccess="Seasonal" /> : null}
            </TableCell>
        )}
        {enableInMarketDate && (
            <TableCell align="center" sx={{ padding: '0 6px' }}>
                <DistributorInMarketDatePicker row={row} handleUpdateRow={handleUpdateRow} />
            </TableCell>
        )}
        {enableInMarketDate && enableSeasonalForecast && (
            <TableCell align="center" sx={{ padding: '0 6px' }}>
                <DistributorEndInMarketDatePicker row={row} handleUpdateRow={handleUpdateRow} />
            </TableCell>
        )}
        <TableCell align="center" sx={{ padding: '0px' }}>
            <TextField
                value={row.customerPartNumber}
                size="small"
                disabled={row?.legal === false}
                onKeyPress={onKeyPressHandler}
                onChange={async (event) => {
                    await handleUpdateRow({ ...row, customerPartNumber: event.target.value })
                }}
                onBlur={onBlurHandler}
                sx={{ width: 130 }}
            />
        </TableCell>
    </React.Fragment>)
}

DistributorTableRow.propTypes = {
    row: PropTypes.object,
    handleUpdateRow: PropTypes.func,
    onSaveProductSettingsDistributorOverride: PropTypes.func,
    isChanged: PropTypes.func,
}